import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Play, BookOpen, Download, CheckCircle, Clock, Award } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CoursePlayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentModule, setCurrentModule] = useState(0);
  const [completedModules, setCompletedModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);

  useEffect(() => {
    fetchCourseAndProgress();
  }, [id]);

  const fetchCourseAndProgress = async () => {
    try {
      // Fetch course details
      const courseResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/courses`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const courseData = await courseResponse.json();
      
      // Find the specific course
      const foundCourse = courseData.courses?.find(c => c.id === id) || mockCourse;
      setCourse(foundCourse);

      // Fetch enrollment progress
      const enrollmentResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/courses/my-enrollments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const enrollmentData = await enrollmentResponse.json();
      
      if (enrollmentData.success) {
        const enrollment = enrollmentData.enrollments?.find(e => e.course_id === id);
        if (enrollment) {
          setCompletedModules(enrollment.completed_modules || []);
        }
      }
    } catch (error) {
      console.error('Error fetching course data:', error);
      setCourse(mockCourse);
    } finally {
      setLoading(false);
    }
  };

  // Mock course data as fallback
  const mockCourse = {
    id: id,
    title: 'Advanced Implant Techniques',
    description: 'Master advanced implant placement and restoration techniques',
    instructor: 'Dr. William Gray',
    duration: '8 hours',
    level: 'Advanced',
    modules: [
      {
        id: 1,
        title: 'Introduction to Advanced Implant Planning',
        type: 'video',
        duration: '45 min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        description: 'Overview of advanced planning techniques and case assessment'
      },
      {
        id: 2,
        title: 'Digital Workflow Integration',
        type: 'video',
        duration: '60 min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        description: 'Learn how to integrate digital tools into your implant workflow'
      },
      {
        id: 3,
        title: 'Course Materials - Implant Guidelines',
        type: 'pdf',
        duration: '30 min read',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        description: 'Comprehensive guide to implant placement protocols'
      },
      {
        id: 4,
        title: 'Complex Case Studies',
        type: 'video',
        duration: '90 min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        description: 'Analysis of complex implant cases and solutions'
      },
      {
        id: 5,
        title: 'Best Practices Documentation',
        type: 'pdf',
        duration: '20 min read',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        description: 'Essential reference guide for implant procedures'
      }
    ]
  };

  const handleModuleComplete = async () => {
    if (!completedModules.includes(currentModule)) {
      const updated = [...completedModules, currentModule];
      setCompletedModules(updated);
      
      // Update progress in backend
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/courses/${id}/progress`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            course_id: id,
            module_id: currentModule,
            completed: true
          })
        });
      } catch (error) {
        console.error('Error updating progress:', error);
      }
    }
    
    if (currentModule < (course?.modules?.length || 5) - 1) {
      setCurrentModule(currentModule + 1);
    }
  };

  const handleUnenroll = async () => {
    if (window.confirm('Are you sure you want to unenroll from this course? Your progress will be saved.')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/courses/${id}/unenroll`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          // Also remove from localStorage
          const enrolled = JSON.parse(localStorage.getItem('enrolledCourses') || '[]');
          const updated = enrolled.filter(courseId => courseId !== id);
          localStorage.setItem('enrolledCourses', JSON.stringify(updated));
          alert('Successfully unenrolled from course');
          navigate('/courses');
        }
      } catch (error) {
        console.error('Error unenrolling:', error);
        alert('Failed to unenroll. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Course Not Found</h2>
          <Button onClick={() => navigate('/courses')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Courses
          </Button>
        </div>
      </div>
    );
  }

  const currentModuleData = course.modules[currentModule];
  const progress = (completedModules.length / course.modules.length) * 100;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/courses')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Courses
        </Button>
        <Button
          variant="outline"
          onClick={handleUnenroll}
          className="border-red-500 text-red-500 hover:bg-red-50"
        >
          Unenroll from Course
        </Button>
      </div>

      {/* Course Header */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-2xl mb-2">{course.title}</CardTitle>
              <p className="text-gray-600 mb-3">{course.description}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <BookOpen className="h-4 w-4 mr-1" />
                  {course.instructor}
                </span>
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {course.duration}
                </span>
                <Badge>{course.level}</Badge>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-[#D4AF37] mb-1">
                {Math.round(progress)}%
              </div>
              <p className="text-sm text-gray-600">Complete</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Video/PDF Player */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  {currentModuleData.type === 'video' ? (
                    <Play className="h-5 w-5 mr-2 text-[#D4AF37]" />
                  ) : (
                    <BookOpen className="h-5 w-5 mr-2 text-[#D4AF37]" />
                  )}
                  Module {currentModule + 1}: {currentModuleData.title}
                </CardTitle>
                {completedModules.includes(currentModule) && (
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Completed
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-600">{currentModuleData.description}</p>
            </CardHeader>
            <CardContent>
              {/* Video Embed */}
              {currentModuleData.type === 'video' && (
                <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
                  <iframe
                    width="100%"
                    height="100%"
                    src={currentModuleData.videoUrl}
                    title={currentModuleData.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              )}

              {/* PDF Embed */}
              {currentModuleData.type === 'pdf' && (
                <div className="mb-4">
                  <div className="aspect-[8.5/11] bg-gray-100 rounded-lg overflow-hidden mb-4">
                    <iframe
                      src={currentModuleData.pdfUrl}
                      title={currentModuleData.title}
                      className="w-full h-full"
                      frameBorder="0"
                    ></iframe>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => window.open(currentModuleData.pdfUrl, '_blank')}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setCurrentModule(Math.max(0, currentModule - 1))}
                  disabled={currentModule === 0}
                >
                  Previous Module
                </Button>
                
                {completedModules.includes(currentModule) ? (
                  currentModule < course.modules.length - 1 ? (
                    <Button
                      className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                      onClick={() => setCurrentModule(currentModule + 1)}
                    >
                      Next Module
                    </Button>
                  ) : (
                    <Button
                      className="bg-green-600 text-white hover:bg-green-700"
                      onClick={() => {
                        alert('Congratulations! Course completed!');
                        navigate('/courses');
                      }}
                    >
                      <Award className="h-4 w-4 mr-2" />
                      Complete Course
                    </Button>
                  )
                ) : (
                  <Button
                    className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                    onClick={handleModuleComplete}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark as Complete
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Module List Sidebar */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Course Modules</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {course.modules.map((module, index) => (
                  <button
                    key={module.id}
                    onClick={() => setCurrentModule(index)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      index === currentModule
                        ? 'border-[#D4AF37] bg-yellow-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          {module.type === 'video' ? (
                            <Play className="h-4 w-4 text-[#D4AF37]" />
                          ) : (
                            <BookOpen className="h-4 w-4 text-[#D4AF37]" />
                          )}
                          <span className="font-semibold text-sm text-[#0A1628]">
                            Module {index + 1}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-1">{module.title}</p>
                        <p className="text-xs text-gray-500">{module.duration}</p>
                      </div>
                      {completedModules.includes(index) && (
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Progress Stats */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Your Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Completed Modules</span>
                <span className="font-semibold text-[#0A1628]">
                  {completedModules.length} / {course.modules.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Current Module</span>
                <span className="font-semibold text-[#D4AF37]">
                  {currentModule + 1}
                </span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-sm text-gray-600">Course Progress</span>
                <span className="font-semibold text-green-600">
                  {Math.round(progress)}%
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CoursePlayer;